/**
 * PlayerProvider - React Context for audio playback management
 * 
 * This context manages all audio playback state and controls:
 * - Play/pause functionality
 * - Seek controls
 * - Volume management
 * - Playback rate adjustment
 * - Position persistence using localStorage
 * 
 * Requirements: 4.1, 4.3, 4.5
 */

import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import type { PlayerState } from '@/types';
import { storageService } from '@/services/storageService';
import { PLAYBACK } from '@/config';

/**
 * Player Context value interface
 * 
 * Provides access to player state and control functions
 */
export interface PlayerContextValue {
  /** Current player state */
  state: PlayerState;
  
  /** 
   * Start playing an episode
   * 
   * @param episodeId - Unique episode identifier
   * @param audioUrl - S3 URL to the audio file
   * @param resetPosition - If true, start from beginning instead of saved position
   * @param storyId - Story identifier for per-story state management
   */
  play: (episodeId: string, audioUrl: string, resetPosition?: boolean, storyId?: string) => void;
  
  /** Pause the current audio */
  pause: () => void;
  
  /** 
   * Seek to a specific time position
   * 
   * @param time - Time in seconds to seek to
   */
  seek: (time: number) => void;
  
  /** 
   * Set the volume level
   * 
   * @param volume - Volume level (0.0 to 1.0)
   */
  setVolume: (volume: number) => void;
  
  /** 
   * Set the playback rate
   * 
   * @param rate - Playback rate (0.5, 1.0, 1.5, 2.0)
   */
  setPlaybackRate: (rate: number) => void;
  
  /** Reference to the HTML audio element */
  audioRef: React.RefObject<HTMLAudioElement | null>;
  
  /** Current story ID for state management */
  currentStoryId: string | null;
}

/**
 * Player Context
 */
const PlayerContext = createContext<PlayerContextValue | undefined>(undefined);

/**
 * PlayerProvider Props
 */
export interface PlayerProviderProps {
  children: React.ReactNode;
}

/**
 * PlayerProvider Component
 * 
 * Wraps the application to provide audio playback functionality.
 * Manages an HTML5 audio element and exposes controls via context.
 * 
 * Requirements: 4.1, 4.3, 4.5
 */
export const PlayerProvider: React.FC<PlayerProviderProps> = ({ children }) => {
  // Audio element reference
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // Track current story ID for per-story state management
  const [currentStoryId, setCurrentStoryId] = useState<string | null>(null);
  
  // Player state
  const [state, setState] = useState<PlayerState>({
    currentEpisodeId: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: PLAYBACK.DEFAULT_VOLUME,
    playbackRate: PLAYBACK.DEFAULT_SPEED,
    loading: false,
    error: null,
  });

  /**
   * Play an episode
   * 
   * Loads the audio file and starts playback. If switching episodes,
   * saves the current position and loads the saved position for the new episode.
   * Now supports per-story state management.
   * 
   * Requirements: 4.1, 4.5
   */
  const play = useCallback((episodeId: string, audioUrl: string, resetPosition: boolean = false, storyId?: string) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Save current position if switching episodes
    if (state.currentEpisodeId && state.currentEpisodeId !== episodeId) {
      storageService.savePlaybackPosition(state.currentEpisodeId, state.currentTime, currentStoryId || undefined);
    }

    // Update current story ID if provided
    if (storyId && storyId !== currentStoryId) {
      setCurrentStoryId(storyId);
    }

    // If same episode and not resetting, just resume
    if (state.currentEpisodeId === episodeId && audio.src && !resetPosition) {
      setState(prev => ({ ...prev, isPlaying: true, error: null }));
      audio.play().catch(error => {
        console.error('[PlayerProvider] Play error:', error);
        setState(prev => ({ ...prev, error, isPlaying: false }));
      });
      return;
    }

    // Load new episode or reset current episode
    setState(prev => ({ 
      ...prev, 
      loading: true, 
      currentEpisodeId: episodeId,
      currentTime: 0,
      error: null 
    }));

    audio.src = audioUrl;
    
    // Load saved position or reset to beginning
    if (resetPosition) {
      audio.currentTime = 0;
    } else {
      const savedPosition = storageService.getPlaybackPosition(episodeId, storyId);
      if (savedPosition > 0) {
        audio.currentTime = savedPosition;
      }
    }

    audio.play().catch(error => {
      console.error('[PlayerProvider] Play error:', error);
      setState(prev => ({ ...prev, error, isPlaying: false, loading: false }));
    });
  }, [state.currentEpisodeId, state.currentTime, currentStoryId]);

  /**
   * Pause playback
   * 
   * Saves the current position to localStorage with story context.
   * 
   * Requirements: 4.1, 4.5
   */
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    setState(prev => ({ ...prev, isPlaying: false }));

    // Save position when pausing
    if (state.currentEpisodeId) {
      storageService.savePlaybackPosition(state.currentEpisodeId, state.currentTime, currentStoryId || undefined);
    }
  }, [state.currentEpisodeId, state.currentTime, currentStoryId]);

  /**
   * Seek to a specific time
   * 
   * Requirements: 4.3
   */
  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Clamp time to valid range
    const clampedTime = Math.max(0, Math.min(time, audio.duration || 0));
    audio.currentTime = clampedTime;
    setState(prev => ({ ...prev, currentTime: clampedTime }));

    // Save position after seeking
    if (state.currentEpisodeId) {
      storageService.savePlaybackPosition(state.currentEpisodeId, clampedTime, currentStoryId || undefined);
    }
  }, [state.currentEpisodeId, currentStoryId]);

  /**
   * Set volume level
   * 
   * Requirements: 4.3
   */
  const setVolume = useCallback((volume: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Clamp volume to valid range
    const clampedVolume = Math.max(0, Math.min(1, volume));
    audio.volume = clampedVolume;
    setState(prev => ({ ...prev, volume: clampedVolume }));
  }, []);

  /**
   * Set playback rate
   * 
   * Requirements: 4.3
   */
  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Validate playback rate
    const validRates = PLAYBACK.SPEEDS;
    const clampedRate = validRates.includes(rate as any) ? rate : PLAYBACK.DEFAULT_SPEED;
    
    audio.playbackRate = clampedRate;
    setState(prev => ({ ...prev, playbackRate: clampedRate }));
  }, []);

  /**
   * Audio event handlers
   * 
   * These handlers update the player state based on audio element events.
   */
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Update current time as audio plays
    const handleTimeUpdate = () => {
      setState(prev => ({ ...prev, currentTime: audio.currentTime }));
    };

    // Update duration when metadata loads
    const handleLoadedMetadata = () => {
      setState(prev => ({ 
        ...prev, 
        duration: audio.duration,
        loading: false 
      }));
    };

    // Handle play event
    const handlePlay = () => {
      setState(prev => ({ ...prev, isPlaying: true, loading: false }));
    };

    // Handle pause event
    const handlePause = () => {
      setState(prev => ({ ...prev, isPlaying: false }));
    };

    // Handle audio end
    const handleEnded = () => {
      setState(prev => ({ ...prev, isPlaying: false, currentTime: 0 }));
      
      // Save final position (0) when episode ends
      if (state.currentEpisodeId) {
        storageService.savePlaybackPosition(state.currentEpisodeId, 0, currentStoryId || undefined);
      }
    };

    // Handle loading state
    const handleWaiting = () => {
      setState(prev => ({ ...prev, loading: true }));
    };

    const handleCanPlay = () => {
      setState(prev => ({ ...prev, loading: false }));
    };

    // Handle errors
    const handleError = () => {
      const error = new Error(audio.error?.message || 'Audio playback error');
      console.error('[PlayerProvider] Audio error:', error);
      setState(prev => ({ 
        ...prev, 
        error, 
        isPlaying: false, 
        loading: false 
      }));
    };

    // Attach event listeners
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('waiting', handleWaiting);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);

    // Cleanup
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('waiting', handleWaiting);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, [state.currentEpisodeId, currentStoryId]);

  /**
   * Periodic position saving
   * 
   * Save playback position at configured interval while playing.
   * This ensures position is saved even if the user closes the browser.
   * 
   * Requirements: 4.5
   */
  useEffect(() => {
    if (!state.isPlaying || !state.currentEpisodeId) return;

    const interval = setInterval(() => {
      if (state.currentEpisodeId) {
        storageService.savePlaybackPosition(state.currentEpisodeId, state.currentTime, currentStoryId || undefined);
      }
    }, PLAYBACK.POSITION_SAVE_INTERVAL);

    return () => clearInterval(interval);
  }, [state.isPlaying, state.currentEpisodeId, state.currentTime, currentStoryId]);

  /**
   * Context value
   */
  const value: PlayerContextValue = {
    state,
    play,
    pause,
    seek,
    setVolume,
    setPlaybackRate,
    audioRef,
    currentStoryId,
  };

  return (
    <PlayerContext.Provider value={value}>
      {/* Hidden audio element managed by the context */}
      <audio ref={audioRef} preload="metadata" />
      {children}
    </PlayerContext.Provider>
  );
};

/**
 * Hook to access the Player context
 * 
 * @throws Error if used outside of PlayerProvider
 * @returns PlayerContextValue
 */
export const usePlayer = (): PlayerContextValue => {
  const context = useContext(PlayerContext);
  
  if (!context) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  
  return context;
};
